import { useEffect, useState, useRef, type FunctionComponent } from "react";
import juriteca from "../../../assets/img/Header/Juriteca.svg";
import { useAuthHook } from "../../hooks/useAuth.hook";
import { useUserData } from "../../hooks/useUserData.hook";
import { storage } from "../../utils/Const.utils";
import type { PerfilEntidad } from "../../types/interfaces/user.interface";
import { RolesMap } from "../../types/maps";
import { StorageKeys } from "../../types/enums/Storage.enum";
import { useConfirmation } from "../../hooks/useConfirmation.hook";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../LanguageSwitcher/LanguageSwitcher";
import ChangePasswordForm from "../ChangePasswordForm/ChangePasswordForm";
import { useGetEntitiesByIdUsuario } from "../../hooks/useUser.hook";
import ConfiguracionPerfil from "../ConfiguracionPerfil/ConfiguracionPerfil";
import { useNavigate } from "react-router";
import { Routes } from "../../types/enums/Routes.enum";
import customAlert from "../CustomAlert";
import ActualizarPerfilModal from "../ActualizarPerfilModal/ActualizarPerfilModal";
import { TipoUsuarioPerfilOpcionEnum } from "@/modules/escritorio/types/enums/TipoUsuario.enum";

interface MenuConfiguracionProps {
    MostrarMenuConfigOpen?: boolean;
    onClose?: () => void;
}

const MenuConfiguracion: FunctionComponent<MenuConfiguracionProps> = ({ MostrarMenuConfigOpen, onClose }) => {

    const [isMobile, setIsMobile] = useState(false);
    const [showLanguageSwitcher, setShowLanguageSwitcher] = useState(false);
    const { logout, user, isLoading } = useAuthHook();
    const idUsuario = parseInt(user?.id || '') || 0;
    const { data: entidades } = useGetEntitiesByIdUsuario(idUsuario);
    const { userData, userImage, } = useUserData();
    const menuRef = useRef<HTMLElement>(null);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const perfil: PerfilEntidad | null = storage.get(StorageKeys.PERFIL);

    // Hook para manejar el modal de configuración personal
    const {
        confirmation: isOpenConfigPerfil,
        confirmated: openConfigPerfil,
        notConfirmated: closeConfigPerfil
    } = useConfirmation({ initialValue: false });

    const {
        confirmation: isOpenChangePassword,
        confirmated: openModalChangePassword,
        notConfirmated: closeModalChangePassword
    } = useConfirmation({initialValue: false});

    const {
        confirmation: isOpenActualizarPerfil,
        confirmated: openModalActualizarPerfil,
        notConfirmated: closeModalActualizarPerfil
    } = useConfirmation({initialValue: false});

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 600);
        };

        checkMobile(); // Verificar al montar
        window.addEventListener('resize', checkMobile);

        return () => {
            window.removeEventListener('resize', checkMobile);
        };
    }, []);

    // Cerrar menú al hacer clic fuera
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!isMobile && menuRef.current && !menuRef.current.contains(event.target as Node)) {
                // Verificar si el clic no es en el botón que abre el menú (el avatar)
                const target = event.target as HTMLElement;
                if (!target.closest('[data-menu-trigger]')) {
                    onClose?.();
                }
            }
        };

        if (MostrarMenuConfigOpen && !isMobile) {
            // Pequeño delay para evitar que se cierre inmediatamente al abrir
            setTimeout(() => {
                document.addEventListener('mousedown', handleClickOutside);
            }, 100);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [MostrarMenuConfigOpen, isMobile, onClose]);

    /* ====== */
    const handleLogout = async () => {
        try {
            await logout();
        } catch (error) {
            console.error('Error during logout:', error);
        }
    };

    const handleChangeProfile = () => {
        storage.remove(StorageKeys.PERFIL);
        navigate(Routes.POST_LOGIN_ROL, { state: { fromChangeProfile: true } });
    };

    // Handler para abrir configuración personal
    const handleOpenConfiguracionPersonal = () => {
        openConfigPerfil();
        // Opcional: cerrar el menú al abrir la configuración
        // onClose?.();
    };

    // Handler para guardar cambios de configuración
    const handleSaveConfiguracion = async (data: any) => {
        try {
            customAlert({
                icon: 'success',
                titulo: t('menu.changesNotSaved'),
                mensaje: t('menu.changesSaved'),
                toast: true,
            });

            closeConfigPerfil();
        } catch (error) {
            customAlert({
                icon: 'error',
                titulo: t('menu.errorSaving'),
                mensaje: t('menu.errorSavingMessage'),
                toast: true,
            });
        }
    };

    // Obtener información del usuario desde useUserData hook
    const getUserInfo = () => {
        //TODO: Usar datos del hook useUserData si están disponibles
        const perfil: PerfilEntidad | null = storage.get(StorageKeys.PERFIL);
        if (userData) {
            const activePerfil = user?.permisos?.find(p => p.Activo);
            return {
                name: userData.fullName,
                role: perfil?.infoEntidad?.relacion?.idRol ? RolesMap.get(perfil?.infoEntidad?.relacion?.idRol) || t('menu.noProfile') : activePerfil?.Perfil || t('menu.noProfile'),
                image: userImage,
                entityName: perfil?.infoEntidad?.nombre || undefined,
                entityImage: perfil?.infoEntidad?.image || undefined,
                idRol: perfil?.infoEntidad?.relacion?.idRol,
            };
        }

        // Fallback usando los datos del user del contexto de auth
        if (!user?.permisos || user.permisos.length === 0) {
            return {
                name: user?.fullName || t('menu.user'),
                role: t('menu.noProfile'),
                image: userImage,
                entityName: undefined,
                entityImage: undefined,
                idRol: undefined,
            };
        }

        const activePerfil = user.permisos.find(p => p.Activo);
        return {
            name: user.fullName || t('menu.user'),
            role: activePerfil?.Perfil || t('menu.noProfile'),
            image: userImage,
            entityName: undefined,
            entityImage: undefined,
            idRol: undefined,
        };
    };

    const userInfo = getUserInfo();
    /* === fin === */
    return (
        <>
            {MostrarMenuConfigOpen &&
                <section ref={menuRef} className={`${isMobile ? 'bg-[#FBFBFB] shadow-lg' : 'absolute top-18 right-10 w-70 h-auto bg-white  rounded-lg shadow-lg z-10'}`}>
                    <ul className="flex flex-col">
                        <li className="flex items-center px-4 py-2 mb-4">
                            {/* <img 
                                className="w-10 h-10 rounded-full border-1 border-[#f4f4f4] hover:cursor-pointer object-cover" 
                                src={userInfo.image} 
                                alt={`Imagen de ${userInfo.name}`}
                                onError={(e) => {
                                    // Fallback en caso de error cargando la imagen
                                    (e.target as HTMLImageElement).src = userImage;
                                }}
                            /> */}
                            <span className="flex flex-col">
                                <span className="flex ml-4 text-gray-700 font-bold text-xs">{userInfo.name?.toUpperCase()}</span>
                                <span className="flex ml-4 text-sm text-gray-400">{userInfo.role}</span>
                            </span>
                        </li>
                        {isMobile &&
                            <li className="flex flex-col justify-start px-4 py-2 mb-4">
                                <span className="text-[#242424]">{t('menu.yourShortcuts')}</span>
                                <span className="flex flex-row justify-center gap-4 max[330px]:gap-2 mt-2">
                                    <span className="flex flex-col items-center gap-2 mt-2  hover:cursor-pointer">
                                        <img className={`w-14 h-14 rounded-full border-3 border-[#0050DA]`} src={juriteca} alt="" />
                                        <span className="bg-[#0050DA] py-[2px] px-[6px] text-white rounded-sm">{t('menu.juriteca')}</span>
                                    </span>
                                    <span className="flex flex-col items-center gap-2 mt-2  hover:cursor-pointer">
                                        <i className="flex items-center justify-center d-icon d-calendar w-14 h-14 text-[24px] text-[#0050DA] rounded-full border-3 border-[#0050DA]"></i>
                                        <span className="text-center bg-[#0050DA] py-[2px] px-[6px] text-white rounded-sm">{t('menu.nationalRole')}</span>
                                    </span>
                                    <span className="flex flex-col items-center gap-2 mt-2  hover:cursor-pointer">
                                        <img className={`w-14 h-14 rounded-full border-3 border-[#0050DA]`} src={juriteca} alt="" />
                                        <span className="bg-[#0050DA] py-[2px] px-[6px] text-white rounded-sm">{t('menu.juriteca')}</span>
                                    </span>
                                </span>
                            </li>
                        }

                        <li
                            className="flex items-center d-icon d-setting text-[#5E5D62] px-4 py-2 hover:bg-gray-100 hover:text-[#0050DA] cursor-pointer border-b border-gray-300"
                            onClick={handleOpenConfiguracionPersonal}
                        >
                            <span className="flex ml-1">{t('menu.personalConfiguration')}</span>
                        </li>
                        <li className="flex items-center d-icon d-password-field text-[#5E5D62] px-4 py-2 hover:bg-gray-100 hover:text-[#0050DA] cursor-pointer"
                            onClick={openModalChangePassword}
                        >
                            <span className="flex ml-1">{t('menu.changePassword')}</span>
                        </li>
                        {(entidades && entidades.length > 0) && (
                        <li className="flex items-center d-icon d-sync text-[#5E5D62] px-4 py-2 hover:bg-gray-100 hover:text-[#0050DA] cursor-pointer"
                            onClick={handleChangeProfile}
                        >
                            <span className="flex ml-1">{t('menu.changeProfile')}</span>
                        </li>)}
                        {(userData?.tipoUsuario === TipoUsuarioPerfilOpcionEnum.CIUDADANO && perfil?.opcion === 1) && (
                        <li className="flex items-center d-icon d-user-setting text-[#5E5D62] px-4 py-2 hover:bg-gray-100 hover:text-[#0050DA] cursor-pointer border-b border-gray-300"
                            onClick={openModalActualizarPerfil}
                        >
                            <span className="flex ml-1">{t('menu.updateProfile')}</span>
                        </li>
                        )}
                        <li 
                            className="flex items-center d-icon d-domain text-[#5E5D62] px-4 py-2 hover:bg-gray-100 hover:text-[#0050DA] cursor-pointer"
                            onClick={() => setShowLanguageSwitcher(!showLanguageSwitcher)}
                        >
                            <span className="flex ml-1">{t('menu.selectLanguage')}</span>
                            {showLanguageSwitcher && (
                                <i className="d-icon d-arrowhead-up ml-auto text-sm"></i>
                            )}
                        </li>
                        {showLanguageSwitcher && (
                            <li className="border-b border-gray-300 bg-gray-50">
                                <LanguageSwitcher 
                                    onClose={() => setShowLanguageSwitcher(false)}
                                />
                            </li>
                        )}
                        <li>
                            <a href={'https://justicia.gob.do/instructivos/index.html'} target="_blank" rel="noopener noreferrer"
                            className="flex items-center d-icon d-help-circle text-[#5E5D62] px-4 py-2 hover:bg-gray-100 hover:text-[#0050DA] cursor-pointer"
                            >
                                <span className="flex ml-1">{t('menu.help')}</span>
                            </a>
                        </li>
                        <li 
                            className="flex items-center d-icon d-exit-to-app text-[#5E5D62] px-4 py-2 hover:bg-gray-100 hover:text-red-600 cursor-pointer transition-colors duration-200"
                            onClick={handleLogout}
                        >
                            <span className="flex ml-1">
                                {isLoading ? t('menu.loggingOut') : t('menu.logout')}
                            </span>
                        </li>
                    </ul>
                </section>
            }

            {
                isOpenConfigPerfil && (
                    <ConfiguracionPerfil
                        open={isOpenConfigPerfil}
                        onClose={closeConfigPerfil}
                    />
                )
            }
            {
                isOpenChangePassword && (
                    <>
                    <ChangePasswordForm isOpen={isOpenChangePassword} closeModal={closeModalChangePassword}/>
                    </>
                )
            }
            {
                isOpenActualizarPerfil && (
                    <ActualizarPerfilModal isOpen={isOpenActualizarPerfil} closeModal={closeModalActualizarPerfil}/>
                )
            }
        </>
    );
}

export default MenuConfiguracion;